import { Edit3, Globe, MessageCircle, Pin, Trash, User, Users } from 'lucide-react'
import Image from 'next/image'
import * as React from 'react'

import { sanitizeHtmlValue } from '@/lib/element-schema-sanitizers'

import { ResolvedUserImage } from '@/components/common/resolved-media-image'
import { Badge } from '@/components/ui/badge'
import { Tabs } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

import { Role } from '@/enums'

import { GroupAvatar } from './group-avatar'
import { UpdateConversationAvatarDialog } from './update-conversation-avatar-dialog'

import { Conversation, ParticipantRole } from '@/types/chat.type'

const ONLINE_WINDOW_MS = 5 * 60 * 1000

interface ChatSidebarProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  conversations: any[]
  selectedConversationId: number | undefined
  unreadCounts: Record<number, number>
  isLoading: boolean
  searchQuery: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSelectConversation: (conversationId: number) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onEditConversationName: (conversation: any) => void
  onDeleteConversation: (conversationId: number) => void
  onPinConversation: (conversationId: number) => void
  onUnpinConversation: (conversationId: number) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getConversationTitle: (conversation: any) => string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getTargetedUser: (conversation: any) => any
  formatTimestamp: (timestamp: string) => string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  authUser: any
  targetUserAvatar: string
  conversationListRef?: React.RefObject<HTMLDivElement>
}

// Helper function to check if user can edit conversation name
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const canEditConversationName = (conversation: any, authUser: any): boolean => {
  if (!authUser || !conversation?.participants) return false

  // Check if authUser is admin
  if (authUser.roles?.includes(Role.ADMIN)) return true

  // Check if authUser is participant with owner role
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentUserParticipant = conversation.participants.find((p: any) => p.user.id === authUser.id)
  return currentUserParticipant?.role === ParticipantRole.OWNER
}

export const ChatSidebar: React.FC<ChatSidebarProps> = ({
  conversations,
  selectedConversationId,
  unreadCounts,
  searchQuery,
  onSelectConversation,
  onEditConversationName,
  onDeleteConversation,
  onPinConversation,
  onUnpinConversation,
  getConversationTitle,
  getTargetedUser,
  formatTimestamp,
  authUser,
  targetUserAvatar,
  conversationListRef,
}) => {
  const isDirectConversation = React.useCallback((conversation: Conversation) => {
    if (!conversation) return false
    if (conversation.type === 'direct') return true
    if (conversation.type === 'group' || conversation.type === 'course' || conversation.type === 'private_group' || conversation.type === 'community') {
      return false
    }
    return Array.isArray(conversation.participants) && conversation.participants.length === 2
  }, [])

  // State to track the current active tab
  const [currentTab, setCurrentTab] = React.useState<'direct' | 'group' | 'community'>('direct')
  const [isManualTabChange, setIsManualTabChange] = React.useState(false)
  const [avatarDialogConversation, setAvatarDialogConversation] = React.useState<Conversation | null>(null)

  // Auto-switch tab based on selected conversation type
  React.useEffect(() => {
    if (selectedConversationId && conversations.length > 0 && !isManualTabChange) {
      const selectedConversation = conversations.find(conv => conv.id === selectedConversationId)
      if (selectedConversation) {
        // Switch to the appropriate tab based on conversation type
        if (
          selectedConversation.type === 'group' ||
          selectedConversation.type === 'course' ||
          selectedConversation.type === 'private_group'
        ) {
          setCurrentTab('group')
        } else if (isDirectConversation(selectedConversation)) {
          setCurrentTab('direct')
        } else if (selectedConversation.type === 'community') {
          setCurrentTab('community')
        }
      }
    }
  }, [selectedConversationId, conversations, isManualTabChange, isDirectConversation])

  // Reset manual tab change flag when selected conversation changes
  React.useEffect(() => {
    setIsManualTabChange(false)
  }, [selectedConversationId])

  // Handle manual tab change
  const handleTabChange = (value: string) => {
    setIsManualTabChange(true)
    setCurrentTab(value as 'direct' | 'group' | 'community')
  }

  // Check if user can manage participants (owner)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const canManageAvatar = (conversation: any): boolean => {
    if (!authUser || !conversation?.participants) return false

    // Check if authUser is participant with owner role
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const currentUserParticipant = conversation.participants.find((p: any) => p.user.id === authUser.id)
    return currentUserParticipant?.role === ParticipantRole.OWNER
  }

  // Handle avatar click
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleAvatarClick = (conversation: any) => {
    setAvatarDialogConversation(conversation)
  }

  // Handle avatar dialog close
  const handleAvatarDialogClose = () => {
    setAvatarDialogConversation(null)
  }

  // Helper function to render a conversation item
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderConversationItem = (conv: any) => (
    <div
      key={conv.id}
      data-conversation-id={conv.id}
      onClick={() => onSelectConversation(conv.id)}
      className={`p-3 rounded-xl cursor-pointer transition-all duration-200 group ${
        selectedConversationId === conv.id
          ? 'bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 shadow-sm'
          : 'hover:bg-gray-50 hover:shadow-sm border border-transparent'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {(() => {
                // For group, course, and community chats, use GroupAvatar component
                if (
                  conv.type === 'group' ||
                  conv.type === 'course' ||
                  conv.type === 'private_group' ||
                  conv.type === 'community'
                ) {
                  const activeParticipants =
                    conv.participants?.filter(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (p: any) => p.status === 'active' && p.user && p.user.id !== authUser?.id
                    ) || []
                  const ownerGroupParticipant = conv.participants.find(
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (p: any) => p.role === ParticipantRole.OWNER
                  )
                  const canManage = canManageAvatar(conv)
                  const shouldShowUploadOption = canManage && !conv.avatar
                  return (
                    <GroupAvatar
                      participants={activeParticipants}
                      size="md"
                      ownerParticipant={ownerGroupParticipant}
                      conversationAvatar={conv.avatar}
                      onClick={canManage ? () => handleAvatarClick(conv) : undefined}
                      showUploadIndicator={shouldShowUploadOption}
                    />
                  )
                }

                // For direct chats, use single user avatar
                const targetedUser = getTargetedUser(conv)
                if (!targetedUser) {
                  if (targetUserAvatar) {
                    return (
                      <Image
                        src={targetUserAvatar}
                        alt="Target user avatar"
                        width={32}
                        height={32}
                        className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover"
                      />
                    )
                  } else {
                    return (
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-300 flex items-center justify-center">
                        <User className="w-4 h-4 sm:w-5 text-gray-600" />
                      </div>
                    )
                  }
                }
                return (
                  <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-gray-300 sm:h-10 sm:w-10">
                    <ResolvedUserImage
                      avatar={targetedUser.user.avatar}
                      alt={`${targetedUser.user.firstName} ${targetedUser.user.lastName}`}
                      size={40}
                      className="h-8 w-8 sm:h-10 sm:w-10"
                      placeholderClassName="h-8 w-8 sm:h-10 sm:w-10"
                    />
                  </div>
                )
              })()}
            </div>
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex min-w-0 items-center gap-2">
                <TooltipProvider>
                  <Tooltip delayDuration={300}>
                    <TooltipTrigger asChild>
                      <div className="flex min-w-0 items-center gap-1">
                        <h5 className="truncate font-medium text-gray-900 cursor-default">
                          {getConversationTitle(conv)}
                        </h5>
                        {conv.participants?.some((participant: { user: { id: number; lastActiveAt?: string | null } | null }) => {
                          if (!participant.user || participant.user.id === authUser?.id || !participant.user.lastActiveAt) {
                            return false
                          }
                          const lastActiveAtMs = new Date(participant.user.lastActiveAt).getTime()
                          if (Number.isNaN(lastActiveAtMs)) {
                            return false
                          }
                          return Date.now() - lastActiveAtMs <= ONLINE_WINDOW_MS
                        }) && (
                          <span
                            className="h-2 w-2 flex-shrink-0 rounded-full bg-green-500"
                            title="Someone is online"
                            aria-label="Someone is online"
                          />
                        )}
                        {conv.isPinned && <Pin className="h-3 w-3 text-blue-500 fill-current flex-shrink-0" />}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <p className="text-sm">{getConversationTitle(conv)}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <p className="text-xs text-gray-500 truncate mt-1">
                {searchQuery.trim() && (
                  <span className="text-blue-600">
                    (
                    {conv.participants?.filter(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (participant: any) =>
                        participant.status === 'active' &&
                        (participant.user.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          participant.user.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          `${participant.user.firstName} ${participant.user.lastName}`
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase()))
                    ).length || 0}{' '}
                    matching)
                  </span>
                )}
              </p>
              {/* Display last message content */}
              {conv.lastMessageContent && (
                <p
                  className="text-xs text-gray-600 truncate mt-1"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(conv.lastMessageContent) }}
                />
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end ml-2 relative">
          {unreadCounts[conv.id] > 0 && (
            <Badge className="mb-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white border-0 shadow-sm">
              {unreadCounts[conv.id]}
            </Badge>
          )}
          {/* Date - Hidden on hover */}
          <div className="group-hover:opacity-0 transition-opacity duration-200">
            {conv.lastMessageAt ? (
              <span className="text-xs text-gray-400 font-medium">{formatTimestamp(conv.lastMessageAt)}</span>
            ) : (
              <span className="text-xs text-gray-400">No messages</span>
            )}
          </div>
          {/* Action buttons - Overlap date on hover */}
          <div className="absolute top-0 right-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all duration-200">
            {/* Pin/Unpin button */}
            {conv.isPinned ? (
              <button
                onClick={e => {
                  e.stopPropagation()
                  onUnpinConversation(conv.id)
                }}
                className="p-1.5 rounded-md transition-colors text-blue-500 hover:text-blue-600 hover:bg-blue-50"
                title="Unpin conversation"
              >
                <Pin className="h-3 w-3 fill-current" />
              </button>
            ) : (
              <button
                onClick={e => {
                  e.stopPropagation()
                  onPinConversation(conv.id)
                }}
                className="p-1.5 rounded-md transition-colors text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                title="Pin conversation"
              >
                <Pin className="h-3 w-3" />
              </button>
            )}
            {canEditConversationName(conv, authUser) && (
              <button
                onClick={e => {
                  e.stopPropagation()
                  onEditConversationName(conv)
                }}
                className="p-1.5 rounded-md transition-colors text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                title="Edit conversation name"
              >
                <Edit3 className="h-3 w-3" />
              </button>
            )}
            <button
              onClick={e => {
                e.stopPropagation()
                onDeleteConversation(conv.id)
              }}
              className="p-1.5 rounded-md transition-colors text-gray-400 hover:text-red-600 hover:bg-red-50"
              title="Remove conversation from your chat list"
            >
              <Trash className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  // Get filtered conversations by type
  const groupConversations = conversations.filter(
    conv => conv.type === 'group' || conv.type === 'course' || conv.type === 'private_group'
  )
  const directConversations = conversations.filter(conv => isDirectConversation(conv))
  const communityConversations = conversations.filter(conv => conv.type === 'community')

  // Helper functions to calculate unread counts for each tab
  const calculateDirectUnreadCount = (): number => {
    return directConversations.reduce((total, conv) => total + (unreadCounts[conv.id] || 0), 0)
  }

  const handleDisplayCount = (count: number) => {
    return count > 9 ? '9+' : count
  }

  const calculateGroupUnreadCount = (): number => {
    return groupConversations.reduce((total, conv) => total + (unreadCounts[conv.id] || 0), 0)
  }

  const calculateCommunityUnreadCount = (): number => {
    return communityConversations.reduce((total, conv) => total + (unreadCounts[conv.id] || 0), 0)
  }

  const tabItems: Array<{
    key: 'direct' | 'group' | 'community'
    label: string
    Icon: React.ComponentType<{ className?: string }>
    count: number
  }> = [
    { key: 'direct', label: 'Direct', Icon: MessageCircle, count: calculateDirectUnreadCount() },
    { key: 'group', label: 'Groups', Icon: Users, count: calculateGroupUnreadCount() },
    { key: 'community', label: 'Community', Icon: Globe, count: calculateCommunityUnreadCount() },
  ]

  return (
    <div className="hidden lg:flex w-96 border-r border-gray-200 flex-col overflow-x-hidden">
      {/* Conversations List */}
      <div ref={conversationListRef} className="flex-1 overflow-y-auto">
        {conversations.length > 0 ? (
          <Tabs value={currentTab} onValueChange={handleTabChange} className="w-full">
            <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-sm border-b border-gray-100 px-2">
              <div className="relative flex items-stretch">
                {tabItems.map(({ key, label, Icon, count }) => {
                  const isActive = currentTab === key
                  return (
                    <button
                      key={key}
                      data-tab={key}
                      onClick={() => handleTabChange(key)}
                      className={`group relative flex flex-1 items-center justify-center gap-1.5 px-3 py-3 text-xs font-medium transition-colors duration-200 ${
                        isActive ? 'text-blue-600' : 'text-gray-500 hover:text-gray-800'
                      }`}
                    >
                      <Icon
                        className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${
                          isActive ? 'scale-110' : 'group-hover:scale-105'
                        }`}
                      />
                      <span className="tracking-tight">{label}</span>
                      {count > 0 && (
                        <Badge
                          variant="secondary"
                          className={`ml-0.5 h-4 min-w-[1rem] rounded-full border-0 px-1.5 text-[10px] font-semibold shadow-sm ${
                            isActive ? 'bg-blue-600 text-white' : 'bg-red-500 text-white'
                          }`}
                        >
                          {handleDisplayCount(count)}
                        </Badge>
                      )}
                      <span
                        className={`pointer-events-none absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-blue-600 transition-all duration-300 ${
                          isActive ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0'
                        }`}
                      />
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="p-3 sm:p-4 pt-4">

            {/* Tab Content */}
            {currentTab === 'group' && (
              <div className="space-y-2">
                {groupConversations.length > 0 ? (
                  groupConversations.map(renderConversationItem)
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="w-8 h-8 text-blue-600" />
                    </div>
                    <p className="text-sm font-medium text-gray-700 mb-1">No group chats yet</p>
                    <p className="text-xs text-gray-500">Create a group chat to start collaborating</p>
                  </div>
                )}
              </div>
            )}

            {currentTab === 'direct' && (
              <div className="space-y-2">
                {directConversations.length > 0 ? (
                  directConversations.map(renderConversationItem)
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <MessageCircle className="w-8 h-8 text-blue-600" />
                    </div>
                    <p className="text-sm font-medium text-gray-700 mb-1">No direct messages yet</p>
                    {authUser.roles.includes(Role.ADMIN) && (
                      <p className="text-xs text-gray-500">Start a conversation with someone</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {currentTab === 'community' && (
              <div className="space-y-2">
                {communityConversations.length > 0 ? (
                  communityConversations.map(renderConversationItem)
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Globe className="w-8 h-8 text-blue-600" />
                    </div>
                    <p className="text-sm font-medium text-gray-700 mb-1">No community chats yet</p>
                    <p className="text-xs text-gray-500">Create a community to start collaborating</p>
                  </div>
                )}
              </div>
            )}
            </div>
          </Tabs>
        ) : searchQuery.trim() ? (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500">No conversations found with users matching "{searchQuery}"</p>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500">No conversations yet</p>
            <p className="text-xs text-gray-400 mt-1">Start a new conversation to begin chatting</p>
          </div>
        )}
      </div>

      {/* Avatar Upload Dialog */}
      {avatarDialogConversation && (
        <UpdateConversationAvatarDialog
          conversation={avatarDialogConversation}
          isOpen={!!avatarDialogConversation}
          onClose={handleAvatarDialogClose}
        />
      )}
    </div>
  )
}
